import { type FC, Fragment } from "react";
import type { TooltipProps } from "recharts";
import type { Payload as TooltipPayload } from "recharts/types/component/DefaultTooltipContent";
import { formatVehicleValue, MARKET_LINE_LABELS, type MarketSeriesKey } from "../../modules/market/market-chart-utils";
import { AreaSeparator } from "../AreaSeparator";
import { TooltipCard } from "./TooltipCard";
import { TooltipItem } from "./TooltipItem";

type MarketTooltipEntry = TooltipPayload<number, string> & {
    dataKey?: MarketSeriesKey;
    value?: number;
};

interface MarketChartTooltipProps extends TooltipProps<number, string> {
    payload?: readonly MarketTooltipEntry[];
    label?: string | number;
}

export const MarketChartTooltip: FC<MarketChartTooltipProps> = ({ active, payload = [], label }) => {
    if (!active || payload.length === 0) {
        return null;
    }

    const numericEntries = payload.filter((entry): entry is MarketTooltipEntry & { value: number } =>
        Boolean(entry && typeof entry.value === "number"),
    );

    if (numericEntries.length === 0) {
        return null;
    }

    const withEnovaEntry = numericEntries.find((entry) => entry.dataKey === "withEnova");
    const withoutEnovaEntry = numericEntries.find((entry) => entry.dataKey === "withoutEnova");
    const difference =
        typeof withEnovaEntry?.value === "number" && typeof withoutEnovaEntry?.value === "number"
            ? withEnovaEntry.value - withoutEnovaEntry.value
            : null;
    const formattedDifference =
        difference !== null && difference !== 0
            ? `${difference >= 0 ? "+" : "-"}${formatVehicleValue(Math.abs(difference))}`
            : undefined;

    return (
        <TooltipCard title={label}>
            {numericEntries.map((entry, index) => {
                const dataKey = entry.dataKey as MarketSeriesKey;
                const labelText = MARKET_LINE_LABELS[dataKey] ?? entry.dataKey;
                const suffix = dataKey === "withEnova" ? formattedDifference : undefined;
                const key = `${String(entry.dataKey ?? "series")}-${index}`;

                return (
                    <Fragment key={key}>
                        <TooltipItem
                            label={String(labelText)}
                            value={entry.value}
                            unit=""
                            formatValue={(value) => formatVehicleValue(value)}
                            suffix={suffix}
                        />
                        {index < numericEntries.length - 1 && <AreaSeparator />}
                    </Fragment>
                );
            })}
        </TooltipCard>
    );
};
